import React from 'react';
import { Form, Button, Space, theme } from 'antd';
import { CodeOutlined } from '@ant-design/icons';
import Editor from '@monaco-editor/react';
import type { SQLEditorConfig, ValueChangeEvent } from '../types';
import { getSQLEditorOptions } from '../types';
import { useDarkMode } from '../../../contexts/DarkModeContext';

interface SQLEditorProps {
  config: SQLEditorConfig;
  value?: string;
  onChange: (event: ValueChangeEvent) => void;
  pageId: string;
  error?: string[];

}

export const SQLEditor: React.FC<SQLEditorProps> = ({
  config,
  value,
  onChange,
  pageId,
  error,
}) => {
  const { isDarkMode } = useDarkMode();
  const { token } = theme.useToken();
  // Extract SQL editor specific options
  const sqlOptions = getSQLEditorOptions(config);
  const handleEditorChange = (newValue: string | undefined) => {
    onChange({
      componentId: config.id,
      value: newValue || '',
      pageId
    });
  };

  const handleGetSelectStatement = () => {
    // This would typically make an API call to generate a SELECT statement
    // based on the selected connection and table
    const selectStatement = `SELECT * FROM ${sqlOptions.tableName || '<table_name>'} WHERE <conditions>`;
    const currentValue = value || '';
    const newValue = currentValue ? `${currentValue}\n${selectStatement}` : selectStatement;
    
    onChange({
      componentId: config.id,
      value: newValue,
      pageId
    });
  };

  const validateRules = () => {
    const rules: any[] = [];
    
    if (config.required) {
      rules.push({
        required: true,
        message: `${config.label} is required`
      });
    }
    
    // Add custom validation rules
    config.validation?.forEach(rule => {
      switch (rule.type) {
        case 'custom':
          rules.push({
            validator: rule.value,
            message: rule.message
          });
          break;
        case 'minLength':
          rules.push({
            min: rule.value,
            message: rule.message
          });
          break;
        // Add more validation types as needed
      }
    });
    
    return rules;
  };

  const formItemProps = {
    label: config.label,
    required: config.required,
    validateStatus: error && error.length > 0 ? 'error' as const : undefined,
    help: error && error.length > 0 ? error[0] : config.description,
    rules: validateRules(),
    labelCol: { span: 24 },
    wrapperCol: { span: 24 }
  };

  return (
    <Form.Item {...formItemProps}>
      <div style={{ border: '1px solid #d9d9d9', borderRadius: '4px' }}>
        {/* Toolbar */}
        {sqlOptions.showToolbar !== false && (
          <div style={{ 
            padding: '8px 12px', 
            borderBottom: '1px solid #f0f0f0', 
            backgroundColor: token.colorBgLayout,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ fontSize: '12px', color: '#666' }}>
              <CodeOutlined /> SQL Editor
            </span>
            <Space>
              {sqlOptions.showGetSelectButton !== false && (
                <Button 
                  size="small" 
                  type="link" 
                  onClick={handleGetSelectStatement}
                  disabled={config.disabled}
                >
                  Get SQL select statement...
                </Button>
              )}
            </Space>
          </div>
        )}
        
        {/* Editor */}
        <Editor
          height={sqlOptions.height || '200px'}
          language="sql"
          theme={isDarkMode ? 'vs-dark' : 'vs-light'}
          value={value || config.defaultValue || ''}
          onChange={handleEditorChange}
          options={{
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            wordWrap: 'on',
            lineNumbers: 'on',
            folding: true,
            selectOnLineNumbers: true,
            roundedSelection: false,
            readOnly: config.disabled || false,
            cursorStyle: 'line',
            fontSize: 14,
            tabSize: 2,
            insertSpaces: true,
            scrollbar: {
              verticalScrollbarSize: 8,
              horizontalScrollbarSize: 8
            }
          }}
        />
      </div>
    </Form.Item>
  );
}; 